import EditIcon from "@mui/icons-material/Edit"
import { Alert, Box, CircularProgress, Grid, MenuItem } from "@mui/material"
import Button from "@mui/material/Button"
import Dialog from "@mui/material/Dialog"
import DialogActions from "@mui/material/DialogActions"
import DialogContent from "@mui/material/DialogContent"
import DialogTitle from "@mui/material/DialogTitle"
import TextField from "@mui/material/TextField"
import { useFormik } from "formik"
import { useAppDispatch, useAppSelector } from "hooks"
import { useEffect, useState } from "react"
import userService from "services/userService"
import { updateUser } from "slices/usersSlice"
import { UserRoles } from "types/userRoles"
import type { IUpdateUser, IUser } from "types/userTypes"
import * as Yup from "yup"

type Props = {
  user: IUser
}

export function UpdateUserDialog({ user }: Props) {
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [error, setError] = useState<string | undefined>(undefined)
  const [open, setOpen] = useState(false)
  const [isDashboardUser, setIsDashboardUser] = useState<boolean>(false)

  const userGroups = useAppSelector((state) => state.usergroup.userGroups)

  const dispatch = useAppDispatch()

  useEffect(() => {
    user.email === undefined || user.email === ""
      ? setIsDashboardUser(false)
      : setIsDashboardUser(true)
  }, [user.email])

  const initialValues: IUpdateUser = {
    userGroupId: user.userGroupId,
    role: user.role,
    pin: user.pin,
    firstname: user.firstname,
    lastname: user.lastname,
    email: user.email,
    phonenumber: user.phonenumber,
    locale: user.locale,
  }

  const updateDashboardUserValidationScheme = Yup.object({
    userGroupId: Yup.string().required("Please select user group"),
    role: Yup.string().required("Please select user role"),
    pin: Yup.string()
      .required("Please enter user PIN")
      .min(3, "Minimum pin length is 3")
      .max(10, "Maximum pin length is 10")
      .matches(/^[0-9]*$/, "PIN can only contain digits."),
    firstname: Yup.string()
      .required("Please enter user first name")
      .max(64, "Maximum length is 64 characters"),
    lastname: Yup.string()
      .required("Please enter user last name")
      .max(64, "Maximum length is 64 characters"),
    email: Yup.string().required().email().max(128, "Maximum length is 128 characters"),
    phonenumber: Yup.string()
      .min(5)
      .matches(
        /^[+][1234567890]+$/,
        "Phone number is not valid. A phone number must start with a plus (+) sign, followed immediately by the country code. A phone number can only contain the + sign and digits.",
      ),
  })

  const updateSimulatorUserValidationScheme = Yup.object({
    userGroupId: Yup.string().required("Please select user group"),
    role: Yup.string().required("Please select user role"),
    pin: Yup.string()
      .required("Please enter user PIN")
      .matches(/^[0-9]*$/, "PIN can only contain digits."),
    firstname: Yup.string().required("Please enter user first name"),
    lastname: Yup.string().required("Please enter user last name"),
    phonenumber: Yup.string()
      .min(5)
      .matches(
        /^[+][1234567890]+$/,
        "Phone number is not valid. A phone number must start with a plus (+) sign, followed immediately by the country code. A phone number can only contain the + sign and digits.",
      ),
  })

  const formik = useFormik({
    initialValues: initialValues,
    validationSchema: isDashboardUser
      ? updateDashboardUserValidationScheme
      : updateSimulatorUserValidationScheme,
    onSubmit: (values) => {
      setIsLoading(true)

      const userUpdate: IUpdateUser = {
        userGroupId: values.userGroupId,
        role: values.role,
        pin: values.pin,
        firstname: values.firstname,
        lastname: values.lastname,
        email: values.email,
        phonenumber: values.phonenumber,
        locale: values.locale,
      }

      userService
        .updateUser(user.id, userUpdate)
        .then((resp) => {
          dispatch(updateUser(resp))
          setIsLoading(false)
          handleClose()
        })
        .catch((err) => {
          console.log(err)
          setIsLoading(false)
          setError("error")
        })
    },
  })

  const handleClickOpen = () => {
    formik.resetForm()
    setOpen(true)
  }

  const handleClose = () => {
    setOpen(false)
  }

  const UpdateSimulatorUser = (
    <Grid container spacing={1}>
      <Grid item xs={12} sm={6}>
        <TextField
          required
          autoFocus
          margin="dense"
          id="firstname"
          label="First name"
          type="firstname"
          fullWidth
          variant="outlined"
          value={formik.values.firstname}
          onChange={formik.handleChange}
          error={formik.touched.firstname && Boolean(formik.errors.firstname)}
          helperText={formik.touched.firstname && formik.errors.firstname}
        />
      </Grid>
      <Grid item xs={12} sm={6}>
        <TextField
          required
          margin="dense"
          id="lastname"
          label="Last name"
          type="lastname"
          fullWidth
          variant="outlined"
          value={formik.values.lastname}
          onChange={formik.handleChange}
          error={formik.touched.lastname && Boolean(formik.errors.lastname)}
          helperText={formik.touched.lastname && formik.errors.lastname}
        />
      </Grid>
      <Grid item xs={12}>
        <TextField
          required
          fullWidth
          margin="dense"
          id="userGroupId"
          label="User Group"
          type="userGroupId"
          name="userGroupId"
          select
          variant="outlined"
          value={formik.values.userGroupId}
          onChange={formik.handleChange}
          error={formik.touched.userGroupId && Boolean(formik.errors.userGroupId)}
          helperText={formik.touched.userGroupId && formik.errors.userGroupId}
        >
          {userGroups.map((userGroup) => (
            <MenuItem key={userGroup.id} value={userGroup.id}>
              {userGroup.name}
            </MenuItem>
          ))}
        </TextField>
      </Grid>
      <Grid item xs={12}>
        <TextField
          required
          margin="dense"
          id="pin"
          label="PIN"
          type="pin"
          fullWidth
          variant="outlined"
          value={formik.values.pin}
          onChange={formik.handleChange}
          error={formik.touched.pin && Boolean(formik.errors.pin)}
          helperText={formik.touched.pin && formik.errors.pin}
        />
      </Grid>
      <Grid item xs={12}>
        <TextField
          margin="dense"
          id="phonenumber"
          label="Phone"
          type="phonenumber"
          fullWidth
          variant="outlined"
          value={formik.values.phonenumber}
          onChange={formik.handleChange}
          error={formik.touched.phonenumber && Boolean(formik.errors.phonenumber)}
          helperText={formik.touched.phonenumber && formik.errors.phonenumber}
        />
      </Grid>
    </Grid>
  )

  const UpdateDashboardUser = (
    <Grid container spacing={1}>
      <Grid item xs={12} sm={6}>
        <TextField
          required
          autoFocus
          margin="dense"
          id="firstname"
          label="First name"
          type="firstname"
          fullWidth
          variant="outlined"
          value={formik.values.firstname}
          onChange={formik.handleChange}
          error={formik.touched.firstname && Boolean(formik.errors.firstname)}
          helperText={formik.touched.firstname && formik.errors.firstname}
        />
      </Grid>
      <Grid item xs={12} sm={6}>
        <TextField
          required
          margin="dense"
          id="lastname"
          label="Last name"
          type="lastname"
          fullWidth
          variant="outlined"
          value={formik.values.lastname}
          onChange={formik.handleChange}
          error={formik.touched.lastname && Boolean(formik.errors.lastname)}
          helperText={formik.touched.lastname && formik.errors.lastname}
        />
      </Grid>
      <Grid item xs={12}>
        <TextField
          required
          fullWidth
          margin="dense"
          id="userGroupId"
          label="User Group"
          type="userGroupId"
          name="userGroupId"
          select
          variant="outlined"
          value={formik.values.userGroupId}
          onChange={formik.handleChange}
          error={formik.touched.userGroupId && Boolean(formik.errors.userGroupId)}
          helperText={formik.touched.userGroupId && formik.errors.userGroupId}
        >
          {userGroups.map((userGroup) => (
            <MenuItem key={userGroup.id} value={userGroup.id}>
              {userGroup.name}
            </MenuItem>
          ))}
        </TextField>
      </Grid>
      <Grid item xs={12}>
        <TextField
          required
          fullWidth
          margin="dense"
          id="role"
          label="Role"
          type="role"
          name="role"
          select
          variant="outlined"
          value={formik.values.role}
          onChange={formik.handleChange}
          error={formik.touched.role && Boolean(formik.errors.role)}
          helperText={formik.touched.role && formik.errors.role}
        >
          <MenuItem key={1} value={UserRoles.USER.toString()}>
            User
          </MenuItem>
          <MenuItem key={2} value={UserRoles.ORGANIZATION_ADMIN.toString()}>
            Admin
          </MenuItem>
        </TextField>
      </Grid>
      <Grid item xs={12}>
        <TextField
          required
          margin="dense"
          id="pin"
          label="PIN"
          type="pin"
          fullWidth
          variant="outlined"
          value={formik.values.pin}
          onChange={formik.handleChange}
          error={formik.touched.pin && Boolean(formik.errors.pin)}
          helperText={formik.touched.pin && formik.errors.pin}
        />
      </Grid>
      <Grid item xs={12}>
        <TextField
          disabled={user.email !== undefined && user.email !== ""}
          required
          margin="dense"
          id="email"
          label="Email (Cannot be changed after created)"
          type="email"
          fullWidth
          variant="outlined"
          value={formik.values.email}
          onChange={formik.handleChange}
          error={formik.touched.email && Boolean(formik.errors.email)}
          helperText={formik.touched.email && formik.errors.email}
        />
      </Grid>
      <Grid item xs={12}>
        <TextField
          margin="dense"
          id="phonenumber"
          label="Phone"
          type="phonenumber"
          fullWidth
          variant="outlined"
          value={formik.values.phonenumber}
          onChange={formik.handleChange}
          error={formik.touched.phonenumber && Boolean(formik.errors.phonenumber)}
          helperText={formik.touched.phonenumber && formik.errors.phonenumber}
        />
      </Grid>
    </Grid>
  )

  return (
    <div>
      <MenuItem onClick={handleClickOpen}>
        <EditIcon sx={{ marginRight: 1 }} />
        Update
      </MenuItem>
      <Dialog open={open} fullWidth maxWidth="sm">
        <form onSubmit={formik.handleSubmit}>
          <Grid container>
            <Grid item xs={6}>
              <DialogTitle>Update User</DialogTitle>
            </Grid>
            {isDashboardUser === false && (
              <Grid item justifyContent="flex-end" xs={6}>
                <Button
                  variant="contained"
                  sx={{ marginTop: 2, marginRight: 2 }}
                  onClick={() => setIsDashboardUser(true)}
                >
                  Add Dashboard Access
                </Button>
              </Grid>
            )}
          </Grid>
          <DialogContent>
            {isDashboardUser === false ? UpdateSimulatorUser : UpdateDashboardUser}
            {error !== undefined && (
              <Alert severity="error">
                Unable to create the user, make sure the email and PIN are not already in use
              </Alert>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={handleClose}>Cancel</Button>
            <Box sx={{ m: 1, position: "relative" }}>
              <Button type="submit" disabled={isLoading}>
                Update
              </Button>
              {isLoading && (
                <CircularProgress
                  size={24}
                  sx={{
                    position: "absolute",
                    top: "50%",
                    left: "50%",
                    marginTop: "-12px",
                    marginLeft: "-12px",
                  }}
                />
              )}
            </Box>
          </DialogActions>
        </form>
      </Dialog>
    </div>
  )
}
